/**
 * The screen model.
 *
 * A screen is declared once and rendered twice: as an HTML panel for an MCP App
 * (`ui://` resource) and as a TUI screen in the terminal. Neither renderer may
 * add data of its own — if a value is not in the model, it does not appear on
 * either surface. That is what keeps the two honest about showing the same
 * thing, which the previous hand-rolled panels did not: `fleet_health_app`
 * served an empty dashboard for months while `fleet_health` reported a full
 * fleet, because they were two separate implementations of the same report.
 *
 * Sections are deliberately few and boring. A screen is a status surface, not a
 * layout engine — anything that cannot be said with a metric, a table, a bar, a
 * key/value list or a note probably should not be on a dashboard.
 */

export type Tone = "ok" | "info" | "warn" | "critical" | "muted";

/** A single headline number. */
export interface Metric {
  label: string;
  value: string | number;
  /** Small qualifier under the value, e.g. "of 100" or "+3 today". */
  hint?: string;
  tone?: Tone;
}

export interface Bar {
  label: string;
  value: number;
  /** Defaults to the largest value in the group. */
  max?: number;
  hint?: string;
  tone?: Tone;
}

export interface KeyValue {
  label: string;
  value: string;
  tone?: Tone;
}

export type Cell = string | number;

export interface TableSection {
  kind: "table";
  title?: string;
  columns: string[];
  rows: Cell[][];
  /** Shown when `rows` is empty — say why, never render a bare empty table. */
  empty?: string;
  /** Column indices to right-align (numbers). */
  numeric?: number[];
}

export type Section =
  | { kind: "metrics"; title?: string; items: Metric[] }
  | { kind: "bars"; title?: string; items: Bar[]; empty?: string }
  | { kind: "keyvalue"; title?: string; items: KeyValue[] }
  | { kind: "note"; tone: Tone; text: string; title?: string }
  | TableSection;

/** A suggested follow-up, rendered as a button in HTML and a hint line in TUI. */
export interface Action {
  label: string;
  /** The tool or CLI command this action maps to. */
  command: string;
  description?: string;
}

export interface Screen {
  /** Stable id — also the ui:// resource slug. */
  id: string;
  title: string;
  subtitle?: string;
  sections: Section[];
  actions?: Action[];
  /** Rendered in the footer; set by the caller, never by a renderer. */
  footer?: string;
}

/** A metric that conveys nothing: zero, a dash, or blank. */
function isBlankValue(value: string | number): boolean {
  const text = String(value).trim();
  return text === "" || text === "—" || text === "-" || Number(text) === 0;
}

/**
 * True when a screen carries no actual data.
 *
 * Judged on values, not structure. A grid of six metric cards all reading `0`
 * is structurally full and informationally empty, and it is exactly the shape
 * that hid the fleet bug for months: the panel looked like a working dashboard
 * reporting a quiet fleet, when in fact no data had arrived at all.
 *
 * A `note` always counts as content — an error or warning is real information,
 * and must never be replaced by the generic empty state.
 */
export function isEmptyScreen(screen: Screen): boolean {
  return screen.sections.every((section) => {
    switch (section.kind) {
      case "metrics":
        return section.items.every((m) => isBlankValue(m.value));
      case "bars":
        return section.items.every((b) => b.value === 0);
      case "keyvalue":
        return section.items.every((kv) => isBlankValue(kv.value));
      case "table":
        return section.rows.length === 0;
      case "note":
        return false;
    }
  });
}

/** Every metric across a screen, for tests and summaries. */
export function screenMetrics(screen: Screen): Metric[] {
  return screen.sections.flatMap((s) => (s.kind === "metrics" ? s.items : []));
}
